import {useCallback, useEffect, useRef, type ClipboardEvent, type KeyboardEvent, type PointerEvent, type SyntheticEvent} from "react";
import "./index.css";
import cn from "classnames";
import {toStoredEditableText, fromStoredEditableText} from "../../libs/editableText";
import {contentEditableHtml, isRichEditableText, storeContentEditableHtml} from "../../libs/contentEditableText";
import {normalizeContentEditableText, toggleModulFont} from "../../libs/contentEditableFont";
import "../ContentEditable/ContentEditable.css";
import "../ContentEditable/FieldColor.css";
import {normalizeContentEditableHtml} from "../../libs/contentEditableLines";
import type {HexTextColor} from "../../libs/textColor";

type CursorProps = {
  x?: number; y?: number; flipX?: boolean; flipY?: boolean; isInteractive?: boolean;
  onMove?: (x: number, y: number) => void; text?: string;
  onTextChange?: (text: string) => void; zoomLevel?: number;
  color?: HexTextColor;
  colorCascade?: boolean;
};

/**
 * DraggableCursor — a decorative cursor/arrow overlay with editable text.
 *
 * Pure rendering + drag component. All controls are managed by the parent.
 *
 * Props:
 * - x, y: position
 * - flipX, flipY: flip state
 * - isInteractive: whether drag is enabled
 * - onMove(x, y): callback when dragged
 * - text: label text (controlled)
 * - onTextChange(text): callback when text edited
 */
const DraggableCursor = ({
  x = 200,
  y = 200,
  flipX = false,
  flipY = false,
  isInteractive = false,
  onMove,
  text = "New Feature",
  onTextChange,
  zoomLevel = 2,
  color,
  colorCascade = false,
}: CursorProps) => {
  const dragRef = useRef({active: false, sx: 0, sy: 0, x0: 0, y0: 0});
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const lastInputTextRef = useRef<string | null>(null);
  const displayText = fromStoredEditableText(text);
  const displayHtml = normalizeContentEditableHtml(contentEditableHtml(displayText, true, false), colorCascade);
  const initialHtml = useRef(displayHtml);

  // React doesn't re-apply children to contentEditable elements after mount.
  // Sync textContent imperatively when the text prop changes externally.
  useEffect(() => {
    if (!textRef.current) return;

    const isFocused = document.activeElement === textRef.current;
    const isLocalTextUpdate = isFocused && lastInputTextRef.current === text;
    if (isLocalTextUpdate) return;

    if (textRef.current.innerHTML !== displayHtml) {
      textRef.current.innerHTML = displayHtml;
    }
  }, [text, displayHtml]);

  const onPointerDown = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (!isInteractive) return;
      if ((e.target as Element).closest(".cursor-text")) return;
      e.preventDefault();
      e.stopPropagation();
      dragRef.current = {active: true, sx: e.clientX, sy: e.clientY, x0: x, y0: y};
      containerRef.current?.setPointerCapture?.(e.pointerId);
    },
    [isInteractive, x, y]
  );

  const onPointerMove = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (!dragRef.current.active) return;
      e.preventDefault();
      const {sx, sy, x0, y0} = dragRef.current;
      const nx = x0 + (e.clientX - sx) * zoomLevel;
      const ny = y0 + (e.clientY - sy) * zoomLevel;
      onMove?.(nx, ny);
    },
    [onMove, zoomLevel]
  );

  const onPointerUp = useCallback(() => {
    dragRef.current.active = false;
  }, []);

  const handleInput = useCallback(
    (e: SyntheticEvent<HTMLElement>) => {
      normalizeContentEditableText(e.currentTarget);
      const rawText = isRichEditableText(displayText) || e.currentTarget.querySelector(".font-modul")
        ? storeContentEditableHtml(e.currentTarget.innerHTML, true) : e.currentTarget.textContent;
      const storedText = toStoredEditableText(rawText);
      lastInputTextRef.current = storedText;
      onTextChange?.(storedText);
    },
    [onTextChange, displayText]
  );

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.altKey && !event.shiftKey &&
      (event.code === "KeyA" || event.key?.toLowerCase() === "a")) {
      event.preventDefault();
      const root = event.currentTarget;
      const selection = root.ownerDocument.getSelection();
      if (selection && toggleModulFont(root, selection)) handleInput(event);
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLElement>) => {
    event.preventDefault();
    const root = event.currentTarget;
    const selection = root.ownerDocument.getSelection();
    if (!selection?.rangeCount) return;
    const range = selection.getRangeAt(0);
    if (!root.contains(range.startContainer) || !root.contains(range.endContainer)) return;
    const node = root.ownerDocument.createTextNode(event.clipboardData.getData("text/plain"));
    range.deleteContents();
    range.insertNode(node);
    selection.setBaseAndExtent(node, node.length, node, node.length);
    handleInput(event);
  };

  const flipSx = flipX ? -1 : 1;
  const flipSy = flipY ? -1 : 1;
  const arrowCornerClass = flipX
    ? flipY
      ? "corner-br"
      : "corner-tr"
    : flipY
      ? "corner-bl"
      : "corner-tl";

  return (
    <div
      className={cn("draggable-cursor", {active: isInteractive})}
      ref={containerRef}
      style={{
        transform: `translate(${x}px, ${y}px)`,
        cursor: isInteractive ? "grab" : "default",
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <div className="cursor-body">
        <div className="cursor-label">
          <span
            ref={textRef}
            className="cursor-text"
            data-field-color={color}
            style={color ? {"--coverbaker-field-color": color} as React.CSSProperties : undefined}
            contentEditable
            suppressContentEditableWarning
            spellCheck={false}
            onPointerDown={(e) => e.stopPropagation()}
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            dangerouslySetInnerHTML={{__html: initialHtml.current}}
          />
        </div>

        <svg
          className={cn("cursor-arrow", arrowCornerClass)}
          width="32"
          height="43"
          viewBox="0 0 48 64"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{transform: `scale(${flipSx}, ${flipSy})`}}
        >
          <path
            d="M4 2L44 36H22L16 62L4 2Z"
            fill="yellow"
            stroke="black"
            strokeWidth="3"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </div>
  );
};

export default DraggableCursor;
